/**
 * ReportView — the purchased Personal Relocation Report document.
 *
 * Renders entirely from the quiz answers snapshot + the same static data
 * files the quiz uses. There is NO server-side generation: the buyer's
 * answers blob is decoded, scoring re-runs locally (pure functions), and
 * every section reads the verified per-country datasets. This keeps the
 * report consistent with the live product and costs zero Worker compute.
 *
 * Print: the page is styled for A4 via Tailwind `print:` variants — the
 * "Download PDF" button is just window.print(); each country section
 * starts on a fresh page.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { countriesQuery, type Country } from '@/data';
import { cn } from '@/components/ui/cn';
import {
  buildCountryNarrative,
  buildPersonaSummary,
  buildTaxAiBlock,
  buildVisaAiBlock,
  describeTopGap,
  deriveWeights,
  getPassport,
  pickBlocker,
  resolveTreatment,
  scoreBand,
  scoreCountries,
  usePassportTreatment,
  useCountryTaxRegimes,
  useCountryVisaExtra,
  useCostOfLiving,
  type Answers,
  type CountryMatchResult,
  type Dimension,
  type QuizResult,
} from '@/features/quiz';
import { topReasonsWithDim } from '@/features/quiz/derive';
import { decodeAnswersFromUrl } from '@/features/quiz/url-codec';
import type { VisaPathway } from '@/features/quiz/types';
import {
  usePropertyTax,
  getCountryTax,
  formatAnnualHeadline,
  formatPurchaseHeadline,
} from '@/features/property-tax';
import {
  useCarOwnership,
  getCarOwnership,
  formatCarTierLabel,
} from '@/features/car-ownership';
import { useCountryNextSteps, PHASE_ORDER } from '@/features/next-steps/data';
import { checklistPath, countryToSlug } from '@/features/next-steps/slugs';
import { buildWikiUrl, type CountryCode as WikiCountryCode } from '@/features/wiki/architecture';
import { isPublishedCountry } from '@/features/wiki/published-countries';
import { ReportPaywall } from './ReportPaywall';

type Lang = 'en' | 'ru';

const DIM_ORDER: Dimension[] = [
  'visa', 'cost', 'tax', 'climate', 'family', 'safety', 'language',
  'healthcare', 'jobs', 'banking',
];

const DIM_BAR_COLORS: Partial<Record<Dimension, string>> = {
  visa: 'bg-terra-600',
  cost: 'bg-amber-600',
  tax: 'bg-emerald-700',
  climate: 'bg-sky-600',
  family: 'bg-violet-600',
  safety: 'bg-teal-700',
  language: 'bg-rose-600',
  healthcare: 'bg-cyan-700',
  jobs: 'bg-indigo-600',
  banking: 'bg-lime-700',
};

export interface ReportViewProps {
  answersBlob: string;
  lang: Lang;
  purchasedAt: string | null;
  /**
   * Locked teaser mode for the free /report/preview surface: renders the
   * cover + only the #1 chapter (with its hard-fact grid and 12-month plan
   * blurred behind a lock), then a ReportPaywall. The paid report passes
   * `locked={false}` (default) and renders everything.
   */
  locked?: boolean;
}

export function ReportView({ answersBlob, lang, purchasedAt, locked = false }: ReportViewProps) {
  const { t } = useTranslation();
  const { data: countriesFile } = useQuery(countriesQuery());
  const { data: passportTreatment } = usePassportTreatment();
  const { data: visaExtra } = useCountryVisaExtra();
  const { data: taxRegimes } = useCountryTaxRegimes();
  const { data: costOfLiving } = useCostOfLiving();

  const answers = useMemo(() => decodeAnswersFromUrl(answersBlob), [answersBlob]);

  const result: QuizResult | null = useMemo(() => {
    if (!answers || !countriesFile) return null;
    return scoreCountries(
      countriesFile.countries,
      answers,
      30,
      passportTreatment,
      visaExtra,
      taxRegimes,
      costOfLiving,
    );
  }, [answers, countriesFile, passportTreatment, visaExtra, taxRegimes, costOfLiving]);

  if (!answers) {
    return (
      <p className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
        {t('report.decodeError', {
          defaultValue:
            'We could not restore the quiz snapshot behind this report. Write to hello@lodestar.place — we will fix it manually.',
        })}
      </p>
    );
  }
  if (!result || !countriesFile) {
    return (
      <p className="font-mono text-sm italic text-ink-400">
        {t('report.loading', { defaultValue: 'Assembling your report…' })}
      </p>
    );
  }

  const byCode = new Map(countriesFile.countries.map((c) => [c.code, c]));
  const top3 = result.topMatches.slice(0, 3);
  const persona = buildPersonaSummary(answers, lang);
  const gap = describeTopGap(top3, lang);
  // Purchases always carry paid_at; the empty-string fallback only guards a
  // malformed row and renders as "Invalid Date"-free blank.
  const dateStr = purchasedAt
    ? new Date(purchasedAt).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <article className="mx-auto max-w-3xl text-ink-700">
      {/* ── Cover ── */}
      <header className="border-b-2 border-ink-900 pb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-terra-600">
          Lodestar · {t('report.kicker', { defaultValue: 'Personal relocation report' })}
        </p>
        <h1 className="mt-2 font-display text-3xl italic text-ink-900">
          {t('report.title', { defaultValue: 'Where you would thrive — and how to get there' })}
        </h1>
        <p className="mt-1 font-mono text-xs text-ink-500">{dateStr}</p>
        {persona.length > 0 && (
          <ul className="mt-4 flex flex-wrap gap-1.5">
            {persona.map((chip) => (
              <li
                key={chip.id}
                className="rounded-full bg-paper-2 px-2.5 py-1 font-mono text-[11px] text-ink-700"
              >
                {chip.label}
              </li>
            ))}
          </ul>
        )}
        {/* Top-3 strip */}
        <ol className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {top3.map((m, i) => {
            const c = byCode.get(m.countryCode);
            return (
              <li
                key={m.countryCode}
                className="flex items-center gap-2 rounded-lg border border-ink-100 bg-paper-2 px-3 py-2"
              >
                <span className="font-mono text-xs text-ink-400">#{i + 1}</span>
                <span aria-hidden="true">{c?.flag_emoji}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                  {lang === 'ru' ? c?.name_ru : c?.name_en}
                </span>
                <span className="font-mono text-sm tabular-nums text-terra-700">
                  {Math.round(m.score * 100)}%
                </span>
              </li>
            );
          })}
        </ol>
        {gap && <p className="mt-2 font-mono text-xs italic text-ink-500">{gap.label}</p>}
      </header>

      {/* ── Country chapters ── */}
      {(locked ? top3.slice(0, 1) : top3).map((m, i) => {
        const c = byCode.get(m.countryCode);
        if (!c) return null;
        return (
          <CountryChapter
            key={m.countryCode}
            rank={i + 1}
            country={c}
            match={m}
            answers={answers}
            result={result}
            lang={lang}
            preview={locked}
          />
        );
      })}

      {/* In locked preview we stop after the #1 chapter and offer the unlock.
          The remaining chapters, comparison table and methodology are the
          paid payload. */}
      {locked ? (
        <ReportPaywall answersBlob={answersBlob} lang={lang} />
      ) : (
      <>
      {/* ── Comparison table ── */}
      <section className="mt-10 break-inside-avoid">
        <h2 className="font-display text-xl italic text-ink-900">
          {t('report.compareTitle', { defaultValue: 'Your top three, side by side' })}
        </h2>
        <table className="mt-3 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-ink-200 text-left font-mono text-[11px] uppercase tracking-wider text-ink-500">
              <th className="py-1.5 pr-2">{t('report.compareDim', { defaultValue: 'Dimension' })}</th>
              {top3.map((m) => {
                const c = byCode.get(m.countryCode);
                return (
                  <th key={m.countryCode} className="py-1.5 pr-2">
                    {c?.flag_emoji} {lang === 'ru' ? c?.name_ru : c?.name_en}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {DIM_ORDER.map((dim) => (
              <tr key={dim} className="border-b border-ink-100">
                <td className="py-1.5 pr-2 text-ink-500">
                  {t(`quiz.result.dim.${dim}`, { defaultValue: dim })}
                </td>
                {top3.map((m) => {
                  const v = m.components[dim];
                  return (
                    <td key={m.countryCode} className="py-1.5 pr-2 font-mono tabular-nums">
                      {v == null ? '—' : `${Math.round(v * 100)}%`}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── Methodology + disclaimer ── */}
      <footer className="mt-10 border-t border-ink-200 pt-4 text-xs text-ink-500">
        <p>
          {t('report.methodology', {
            defaultValue:
              'Scores combine your quiz answers with per-country datasets (visa programs, tax regimes, property costs, car ownership, passport treatment) maintained by the Lodestar editorial team. Percentages are relative fit for YOUR answers, not absolute country grades.',
          })}
        </p>
        <p className="mt-2">
          {t('report.disclaimer', {
            defaultValue:
              'This report is guidance, not legal, tax, or immigration advice. Rules change; verify deadlines and amounts with official sources or a licensed professional before acting.',
          })}
        </p>
      </footer>
      </>
      )}
    </article>
  );
}

// ─── Per-country chapter ─────────────────────────────────────────────────────

function CountryChapter({
  rank,
  country,
  match,
  answers,
  result,
  lang,
  preview = false,
}: {
  rank: number;
  country: Country;
  match: CountryMatchResult;
  answers: Answers;
  result: QuizResult;
  lang: Lang;
  /** Free preview: blur the hard-fact grid + 12-month plan behind a lock. */
  preview?: boolean;
}) {
  const { t } = useTranslation();
  const { data: visaExtra } = useCountryVisaExtra();
  const { data: taxRegimes } = useCountryTaxRegimes();
  const { data: passportTreatment } = usePassportTreatment();
  const { data: propertyTax } = usePropertyTax();
  const { data: carFile } = useCarOwnership();
  const { data: costOfLiving } = useCostOfLiving();
  const { data: nextSteps } = useCountryNextSteps(country.code);

  const weights = result.weights ?? deriveWeights(answers);
  const narrativeResult = buildCountryNarrative(country, match, answers, weights, lang, costOfLiving);
  const narrative = narrativeResult.sentences;
  const showCostDisclaimer = narrativeResult.usedDimensions.includes('cost');
  const reasons = topReasonsWithDim(match, weights, t);
  const blocker = pickBlocker(match, t, answers);
  const band = scoreBand(match.score);

  const passport = getPassport(answers);
  const treatment = resolveTreatment(passport, country.code, passportTreatment, lang);
  const selectedPathways = (answers.visa_pathway?.optionIds ?? []) as VisaPathway[];
  const visaBlock = buildVisaAiBlock(visaExtra, country.code, selectedPathways, lang);
  const taxBlock = buildTaxAiBlock(taxRegimes, country.code, lang);
  const propTax = getCountryTax(propertyTax, country.code);
  const car = getCarOwnership(carFile, country.code);

  const name = lang === 'ru' ? country.name_ru : country.name_en;
  const published = isPublishedCountry(country.code);
  const guideUrl = published
    ? buildWikiUrl({ lang, cc: country.code as WikiCountryCode })
    : null;
  const checklistSlug = countryToSlug(country.code);
  const checklistUrl = checklistSlug ? checklistPath(checklistSlug) : null;

  // phases is a Record keyed by phase id. Italy's checklist data lives in
  // hardcoded constants inside NextStepsPage (the hook is disabled for IT),
  // so for IT we render the standard 5-phase skeleton from i18n titles only.
  const phasesRec = nextSteps?.phases ?? null;

  return (
    <section className="mt-10 break-before-page">
      <header className="flex items-baseline gap-3 border-b border-ink-200 pb-3">
        <span className="font-mono text-sm text-ink-400">#{rank}</span>
        <h2 className="font-display text-2xl italic text-ink-900">
          {country.flag_emoji} {name}
        </h2>
        <span className="ml-auto font-mono text-lg tabular-nums text-terra-700">
          {Math.round(match.score * 100)}%
        </span>
        <span className="rounded-full bg-paper-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-500">
          {t(`report.band.${band}`, { defaultValue: band })}
        </span>
      </header>

      {/* Narrative */}
      {narrative.length > 0 && (
        <div className="mt-4 space-y-2 text-sm leading-relaxed">
          {narrative.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
          {showCostDisclaimer && (
            <p className="font-mono text-[10px] leading-snug text-ink-400">
              {t('quiz.cost.disclaimer')}
            </p>
          )}
        </div>
      )}

      {/* Why it fits / watch out */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {reasons.length > 0 && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-emerald-800">
              {t('report.whyFits', { defaultValue: 'Why it fits you' })}
            </h3>
            <ul className="mt-1.5 list-inside list-disc space-y-1 text-sm">
              {reasons.map((r) => (
                <li key={r.dim}>{r.text}</li>
              ))}
            </ul>
          </div>
        )}
        {blocker && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-amber-800">
              {t('report.watchOut', { defaultValue: 'Watch out for' })}
            </h3>
            <p className="mt-1.5 text-sm">{blocker}</p>
          </div>
        )}
      </div>

      {/* Dimension bars */}
      <div className="mt-4 break-inside-avoid">
        <h3 className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {t('report.dimsTitle', { defaultValue: 'Fit by dimension' })}
        </h3>
        <ul className="mt-2 space-y-1.5">
          {DIM_ORDER.filter((d) => match.components[d] != null).map((dim) => {
            const v = match.components[dim] ?? 0;
            return (
              <li key={dim} className="flex items-center gap-2 text-xs">
                <span className="w-24 shrink-0 text-ink-500">
                  {t(`quiz.result.dim.${dim}`, { defaultValue: dim })}
                </span>
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-ink-100">
                  <span
                    className={cn('block h-full rounded-full', DIM_BAR_COLORS[dim] ?? 'bg-ink-400')}
                    style={{ width: `${Math.round(v * 100)}%` }}
                  />
                </span>
                <span className="w-10 text-right font-mono tabular-nums">
                  {Math.round(v * 100)}%
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Hard facts + 12-month plan — the paid depth. In the free preview
          these are blurred behind a lock: the reader sees there's real
          substance (income floors, tax regime, budget, plan) without getting
          the numbers. */}
      <div className={preview ? 'relative' : 'contents'}>
        {preview && <PreviewLock />}
        <div
          className={preview ? 'pointer-events-none select-none blur-[5px]' : 'contents'}
          aria-hidden={preview || undefined}
        >
      {/* Facts grid: visa / tax / property / car / passport */}
      <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {visaBlock?.top_pathway && (
          <FactCard label={t('report.visaPath', { defaultValue: 'Your strongest visa path' })}>
            <strong>{visaBlock.top_pathway.name}</strong>
            {visaBlock.top_pathway.min_income_eur != null && (
              <>
                {' — '}
                {t('report.visaIncomeFloor', {
                  defaultValue: 'income floor €{{amount}}/mo',
                  amount: visaBlock.top_pathway.min_income_eur.toLocaleString(),
                })}
              </>
            )}
          </FactCard>
        )}
        {taxBlock && (
          <FactCard label={t('report.taxes', { defaultValue: 'Income tax' })}>
            {taxBlock.pit_rate_pct != null && (
              <>
                {t('report.taxPit', { defaultValue: 'Personal income tax up to' })}{' '}
                <strong>{taxBlock.pit_rate_pct}%</strong>
              </>
            )}
            {taxBlock.special_regime && (
              <>
                {'. '}
                {t('report.taxRegime', { defaultValue: 'Special regime:' })}{' '}
                <strong>{taxBlock.special_regime}</strong>
              </>
            )}
          </FactCard>
        )}
        {propTax && formatAnnualHeadline(propTax, lang) && (
          <FactCard label={t('report.property', { defaultValue: 'Property taxes' })}>
            {formatAnnualHeadline(propTax, lang)}
            {formatPurchaseHeadline(propTax, lang) && (
              <> · {formatPurchaseHeadline(propTax, lang)}</>
            )}
          </FactCard>
        )}
        {car && (
          <FactCard label={t('report.car', { defaultValue: 'Car ownership' })}>
            {formatCarTierLabel(car.tier, lang)}
            {car.monthly_total_est_eur != null && (
              <> · ≈€{car.monthly_total_est_eur.toLocaleString()}/{t('report.perMonth', { defaultValue: 'mo' })}</>
            )}
          </FactCard>
        )}
        {treatment && treatment.status !== 'welcoming' && (
          <FactCard
            label={t('report.passportNote', { defaultValue: 'Passport-specific note' })}
            tone="warn"
          >
            {treatment.evidence}
          </FactCard>
        )}
      </dl>

      {/* 12-month plan */}
      <div className="mt-5 break-inside-avoid">
        <h3 className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {t('report.planTitle', { defaultValue: 'Your first 12 months — the phases' })}
        </h3>
        <ol className="mt-2 space-y-1.5 text-sm">
          {PHASE_ORDER.map((pid, i) => {
            const items = phasesRec?.[pid] ?? [];
            const first = items[0];
            const title = t(`nextSteps.phase.${pid}.title`, { defaultValue: pid });
            const timeline = t(`nextSteps.phase.${pid}.timeline`, { defaultValue: '' });
            const firstLabel = first ? (lang === 'ru' ? first.label_ru : first.label_en) : null;
            return (
              <li key={pid} className="flex gap-2">
                <span className="font-mono text-xs text-terra-600">{i + 1}.</span>
                <span>
                  <strong>{title}</strong>
                  {timeline && <span className="font-mono text-xs text-ink-400"> · {timeline}</span>}
                  {firstLabel && (
                    <span className="text-ink-500">
                      {' '}— {t('report.planStartsWith', { defaultValue: 'starts with' })}{' '}
                      {firstLabel}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
        </div>
      </div>

      {/* Links — hidden in the preview so the only forward action is the
          paywall, not a free trip to the guide/checklist. */}
      <p className={cn('mt-4 flex flex-wrap gap-3 text-sm print:hidden', preview && 'hidden')}>
        {guideUrl && (
          <Link to={guideUrl} className="font-medium text-terra-700 underline hover:text-terra-800">
            {t('report.guideLink', { defaultValue: 'Read the full {{name}} guide', name })} →
          </Link>
        )}
        {checklistUrl && (
          <Link
            to={checklistUrl}
            className="font-medium text-terra-700 underline hover:text-terra-800"
          >
            {t('report.checklistLink', { defaultValue: 'Open the interactive checklist' })} →
          </Link>
        )}
      </p>
    </section>
  );
}

/** Centered lock badge over the blurred paid-depth block in preview mode. */
function PreviewLock() {
  const { t } = useTranslation();
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-terra-600 bg-paper px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-terra-700 shadow-sm">
        <span aria-hidden="true">🔒</span>
        {t('report.locked.factHint', { defaultValue: 'Income floor · tax · budget · plan — in your report' })}
      </span>
    </div>
  );
}

function FactCard({
  label,
  tone = 'default',
  children,
}: {
  label: string;
  tone?: 'default' | 'warn';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'break-inside-avoid rounded-lg border p-3',
        tone === 'warn' ? 'border-amber-200 bg-amber-50/50' : 'border-ink-100 bg-paper-2',
      )}
    >
      <dt className="font-mono text-[11px] uppercase tracking-wider text-ink-500">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}
